import { v4 as uuidv4 } from 'uuid';
import { getAllSegments } from './compositeUtils';
import { getSegmentsTrack } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/utils';
import { Clip } from '@/domains/asset';
import { Segment } from '@/App/remotion/MainPlayer/types/Segment/Segment';
import { Element } from '@/App/remotion/MainPlayer/types/Element/Element';
import { ElementsTrack } from '@/App/remotion/MainPlayer/types/Track/ElementsTrack';
import { TrackType } from '@/App/remotion/MainPlayer/types/Track/Type';
import { approxGTE, approxLTE } from '@/libs/compare';
import { getClipTotalDurationInFrames } from '@/App/remotion/MainPlayer/utils/configUtils';

const MAX_ELEMENT_TRACKS = 50;
interface SegmentBounds {
  id: string;
  fromFrame: number;
  toFrame: number;
}

interface ElementWithinSegment {
  element: Element;
  trackId: string;
  trackIndex: number;
  elementIndex: number;
  overlapType: 'full' | 'partial-start' | 'partial-end' | 'partial-both';
  overlapFromFrame: number;
  overlapToFrame: number;
}

interface ElementWithTrackInfo {
  element: Element;
  originalTrackId: string;
}

const getElementsTracks = (clip: Clip): ElementsTrack[] => {
  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    return [];
  }
  return playerConfig.playerConfig.tracks.filter(track => track.type === TrackType.ELEMENTS) as ElementsTrack[];
};

const getSegmentBounds = (segments: Segment[]): SegmentBounds[] => {
  return segments.map(segment => ({
    id: segment.id,
    fromFrame: segment.fromFrame,
    toFrame: segment.toFrame
  }));
};

const hasTimeOverlap = (
  element1: { fromFrame: number; toFrame: number },
  element2: { fromFrame: number; toFrame: number }
): boolean => {
  return !(approxLTE(element1.toFrame, element2.fromFrame) || approxLTE(element2.toFrame, element1.fromFrame));
};

const canFitInTrack = (element: { fromFrame: number; toFrame: number }, trackElements: Element[]): boolean => {
  return !trackElements.some(existingElement => hasTimeOverlap(existingElement, element));
};

const createNewElementsTrack = (): ElementsTrack => {
  const trackId = uuidv4();
  return {
    id: trackId,
    name: `Track - ${trackId}`,
    type: TrackType.ELEMENTS,
    hideTrack: { inTimeline: false, inPlayer: false },
    elements: []
  };
};

const findOrCreateTrackForElement = (
  element: Element,
  tracks: ElementsTrack[],
  preferredTrackId?: string
): { track: ElementsTrack; isNew: boolean } => {
  if (preferredTrackId) {
    const preferredTrack = tracks.find(t => t.id === preferredTrackId);
    if (preferredTrack && canFitInTrack(element, preferredTrack.elements)) {
      return { track: preferredTrack, isNew: false };
    }
  }

  for (const track of tracks) {
    if (canFitInTrack(element, track.elements)) {
      return { track, isNew: false };
    }
  }

  const newTrack = createNewElementsTrack();
  if (tracks.length > MAX_ELEMENT_TRACKS) {
    console.error('Too many tracks', tracks.length);
  }
  tracks.push(newTrack);
  return { track: newTrack, isNew: true };
};

const findElementsWithinSegment = (
  clip: Clip,
  segmentFromFrame: number,
  segmentToFrame: number
): ElementWithinSegment[] => {
  const elementsTracks = getElementsTracks(clip);
  const totalDuration = getClipTotalDurationInFrames(clip);
  const elementsWithinSegment: ElementWithinSegment[] = [];

  elementsTracks.forEach((track, trackIndex) => {
    track.elements.forEach((element, elementIndex) => {
      const elementDuration = element.toFrame - element.fromFrame;
      const spansMoreThan50Percent = elementDuration > totalDuration * 0.5;

      if (spansMoreThan50Percent) {
        return;
      }

      const overlapStart = Math.max(element.fromFrame, segmentFromFrame);
      const overlapEnd = Math.min(element.toFrame, segmentToFrame);

      if (approxGTE(overlapStart, overlapEnd)) {
        return;
      }

      let overlapType: ElementWithinSegment['overlapType'];

      const startsWithinSegment = approxGTE(element.fromFrame, segmentFromFrame);
      const endsWithinSegment = approxLTE(element.toFrame, segmentToFrame);

      if (startsWithinSegment && endsWithinSegment) {
        overlapType = 'full';
      } else if (!startsWithinSegment && !endsWithinSegment) {
        overlapType = 'partial-both';
      } else if (!startsWithinSegment) {
        overlapType = 'partial-start';
      } else {
        overlapType = 'partial-end';
      }

      elementsWithinSegment.push({
        element,
        trackId: track.id,
        trackIndex,
        elementIndex,
        overlapType,
        overlapFromFrame: overlapStart,
        overlapToFrame: overlapEnd
      });
    });
  });

  return elementsWithinSegment;
};

const duplicateElement = (element: Element): Element => {
  return {
    ...structuredClone(element),
    id: uuidv4()
  };
};

const splitElementForSegment = (
  elementInfo: ElementWithinSegment,
  segmentFromFrame: number,
  segmentToFrame: number
): { movedPart: ElementWithTrackInfo; remainingParts: ElementWithTrackInfo[] } => {
  const { element, overlapType, overlapFromFrame, overlapToFrame, trackId } = elementInfo;

  const movedPart = duplicateElement(element);
  movedPart.fromFrame = overlapFromFrame;
  movedPart.toFrame = overlapToFrame;

  const remainingParts: ElementWithTrackInfo[] = [];

  if (overlapType === 'partial-start' || overlapType === 'partial-both') {
    const leftPart = duplicateElement(element);
    leftPart.fromFrame = element.fromFrame;
    leftPart.toFrame = segmentFromFrame;
    if (leftPart.toFrame > leftPart.fromFrame) {
      remainingParts.push({ element: leftPart, originalTrackId: trackId });
    }
  }

  if (overlapType === 'partial-end' || overlapType === 'partial-both') {
    const rightPart = duplicateElement(element);
    rightPart.fromFrame = segmentToFrame;
    rightPart.toFrame = element.toFrame;
    if (rightPart.toFrame > rightPart.fromFrame) {
      remainingParts.push({ element: rightPart, originalTrackId: trackId });
    }
  }

  return { movedPart: { element: movedPart, originalTrackId: trackId }, remainingParts };
};

const calculateElementShift = (
  elementFromFrame: number,
  elementToFrame: number,
  oldSegmentBounds: SegmentBounds[],
  newSegmentBounds: SegmentBounds[]
): { newFromFrame: number; newToFrame: number } => {
  let newFromFrame = elementFromFrame;
  let newToFrame = elementToFrame;

  for (let i = 0; i < oldSegmentBounds.length; i++) {
    const oldBounds = oldSegmentBounds[i];
    const newBounds = newSegmentBounds.find(b => b.id === oldBounds.id);

    if (!newBounds) {
      continue;
    }

    const shift = newBounds.fromFrame - oldBounds.fromFrame;

    const fromFrameInSegment = approxGTE(elementFromFrame, oldBounds.fromFrame) && elementFromFrame < oldBounds.toFrame;
    const toFrameInSegment = elementToFrame > oldBounds.fromFrame && approxLTE(elementToFrame, oldBounds.toFrame);
    const fullyContained =
      approxGTE(elementFromFrame, oldBounds.fromFrame) && approxLTE(elementToFrame, oldBounds.toFrame);

    if (fromFrameInSegment) {
      const offsetWithinSegment = elementFromFrame - oldBounds.fromFrame;
      newFromFrame = newBounds.fromFrame + offsetWithinSegment;
    }

    if (toFrameInSegment) {
      const offsetWithinSegment = elementToFrame - oldBounds.fromFrame;
      newToFrame = newBounds.fromFrame + offsetWithinSegment;
    }

    if (fullyContained) {
      newFromFrame = elementFromFrame + shift;
      newToFrame = elementToFrame + shift;
      break;
    }
  }

  return { newFromFrame, newToFrame };
};

const updateElementTimingsWithTrackAssignment = (
  elementsTracks: ElementsTrack[],
  elementsToMove: Map<string, ElementWithTrackInfo>,
  remainingElements: Map<string, ElementWithTrackInfo[]>,
  oldSegmentBounds: SegmentBounds[],
  newSegmentBounds: SegmentBounds[],
  activeOldBounds: SegmentBounds,
  activeNewBounds: SegmentBounds,
  totalDurationFrames: number
): ElementsTrack[] => {
  const updatedTracks: ElementsTrack[] = structuredClone(elementsTracks);
  const elementsToReassign: { element: Element; preferredTrackId: string }[] = [];

  updatedTracks.forEach(track => {
    const newElements: Element[] = [];

    track.elements.forEach(element => {
      if (elementsToMove.has(element.id)) {
        const movedInfo = elementsToMove.get(element.id)!;
        const movedElement = movedInfo.element;
        const offsetWithinActiveSegment = movedElement.fromFrame - activeOldBounds.fromFrame;
        const newFromFrame = activeNewBounds.fromFrame + offsetWithinActiveSegment;
        const duration = movedElement.toFrame - movedElement.fromFrame;

        const updatedMovedElement = {
          ...movedElement,
          fromFrame: newFromFrame,
          toFrame: newFromFrame + duration
        };

        if (canFitInTrack(updatedMovedElement, newElements)) {
          newElements.push(updatedMovedElement);
        } else {
          elementsToReassign.push({ element: updatedMovedElement, preferredTrackId: track.id });
        }

        const remaining = remainingElements.get(element.id);
        if (remaining) {
          remaining.forEach(remainingInfo => {
            const { newFromFrame: adjFrom, newToFrame: adjTo } = calculateElementShift(
              remainingInfo.element.fromFrame,
              remainingInfo.element.toFrame,
              oldSegmentBounds,
              newSegmentBounds
            );
            const updatedRemainingElement = {
              ...remainingInfo.element,
              fromFrame: adjFrom,
              toFrame: adjTo
            };

            if (canFitInTrack(updatedRemainingElement, newElements)) {
              newElements.push(updatedRemainingElement);
            } else {
              elementsToReassign.push({
                element: updatedRemainingElement,
                preferredTrackId: remainingInfo.originalTrackId
              });
            }
          });
        }
      } else {
        const elementDuration = element.toFrame - element.fromFrame;
        const spansMoreThan50Percent = elementDuration > totalDurationFrames * 0.5;

        let updatedElement: Element;
        if (spansMoreThan50Percent) {
          updatedElement = { ...element };
        } else {
          const { newFromFrame, newToFrame } = calculateElementShift(
            element.fromFrame,
            element.toFrame,
            oldSegmentBounds,
            newSegmentBounds
          );
          updatedElement = { ...element, fromFrame: newFromFrame, toFrame: newToFrame };
        }

        if (canFitInTrack(updatedElement, newElements)) {
          newElements.push(updatedElement);
        } else {
          elementsToReassign.push({ element: updatedElement, preferredTrackId: track.id });
        }
      }
    });

    track.elements = newElements.filter(el => el.toFrame > el.fromFrame && approxGTE(el.fromFrame, 0));
  });

  elementsToReassign.forEach(({ element, preferredTrackId }) => {
    if (approxLTE(element.toFrame, element.fromFrame) || element.fromFrame < 0) {
      return;
    }

    const { track } = findOrCreateTrackForElement(element, updatedTracks, preferredTrackId);
    track.elements.push(element);
  });

  return updatedTracks.filter(track => track.elements.length > 0);
};

export const reorderSegment = ({
  newClip,
  activeId,
  overId
}: {
  newClip: Clip;
  activeId: string;
  overId: string;
}): void => {
  const playerConfig = newClip.asset_metadata.player_config;
  if (!playerConfig) {
    return;
  }

  const allSegments = getAllSegments(newClip);
  const activeIndex = allSegments.findIndex(segment => segment.id === activeId);
  const overIndex = allSegments.findIndex(segment => segment.id === overId);

  if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) {
    return;
  }

  const activeSegment = allSegments[activeIndex];
  const oldSegmentBounds = getSegmentBounds(allSegments);
  const activeOldBounds = oldSegmentBounds[activeIndex];

  const elementsWithinActive = findElementsWithinSegment(newClip, activeSegment.fromFrame, activeSegment.toFrame);

  const elementsToMove = new Map<string, ElementWithTrackInfo>();
  const remainingElements = new Map<string, ElementWithTrackInfo[]>();

  elementsWithinActive.forEach(elementInfo => {
    if (elementInfo.overlapType === 'full') {
      elementsToMove.set(elementInfo.element.id, {
        element: elementInfo.element,
        originalTrackId: elementInfo.trackId
      });
    } else {
      const { movedPart, remainingParts } = splitElementForSegment(
        elementInfo,
        activeSegment.fromFrame,
        activeSegment.toFrame
      );
      elementsToMove.set(elementInfo.element.id, movedPart);
      if (remainingParts.length > 0) {
        remainingElements.set(elementInfo.element.id, remainingParts);
      }
    }
  });

  const segmentsTrack = getSegmentsTrack(playerConfig);
  const [removed] = segmentsTrack.segments.splice(activeIndex, 1);
  segmentsTrack.segments.splice(overIndex, 0, removed);

  let currentFrame = 0;
  const newSegmentBounds = getSegmentBounds(segmentsTrack.segments);
  newSegmentBounds.forEach(segment => {
    const duration = segment.toFrame - segment.fromFrame;
    segment.fromFrame = currentFrame;
    segment.toFrame = currentFrame + duration;
    currentFrame += duration;
  });

  const activeNewBounds = newSegmentBounds.find(b => b.id === activeId)!;

  const elementsTracks = getElementsTracks(newClip);
  const totalDurationFrames = getClipTotalDurationInFrames(newClip);
  const updatedElementsTracks = updateElementTimingsWithTrackAssignment(
    elementsTracks,
    elementsToMove,
    remainingElements,
    oldSegmentBounds,
    newSegmentBounds,
    activeOldBounds,
    activeNewBounds,
    totalDurationFrames
  );

  const nonElementTracks = playerConfig.playerConfig.tracks.filter(track => track.type !== TrackType.ELEMENTS);
  playerConfig.playerConfig.tracks = [...nonElementTracks, ...updatedElementsTracks];
};
